import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { Container, LogOut, AlertTriangle, Camera, Plus, FileText, HelpCircle } from "lucide-react";
import { signOut } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { SkipMark } from "@/components/skip-mark";
import { CoachTour } from "@/components/coach-tour";
import {
  markTourAutoShown,
  safeLocalStorage,
  shouldShowTour,
  tourAutoShownThisSession,
} from "@/lib/tour";
import { describeError, useLoadsList } from "@/lib/api";
import { formatAmount } from "@/lib/format";
import {
  buildLoadListRows,
  countNeedingAmount,
  sumPricedAmount,
  type LoadListDisplayRow,
} from "@/lib/selectors";

// `userId` is injected by App from the session rather than read here with
// useSession, mirroring how Report takes its `today` — it keeps this screen
// a pure function of its props and its one query.
export function List({ userId }: { userId?: string | undefined } = {}) {
  const { data, isPending, isError, error } = useLoadsList();

  const storage = useMemo(() => safeLocalStorage(), []);
  const [tourOpen, setTourOpen] = useState(false);

  // Auto-show once, on the driver's first landing. Both guards matter: the
  // stored flag across sessions, the in-memory one within this session.
  useEffect(() => {
    if (tourAutoShownThisSession()) return;
    if (!shouldShowTour(userId, storage)) return;
    markTourAutoShown();
    setTourOpen(true);
  }, [userId, storage]);

  const rows = data ? buildLoadListRows(data.loads) : [];
  const needAmount = countNeedingAmount(rows);
  const pricedTotal = data ? sumPricedAmount(data.loads) : 0;

  return (
    <div className="min-h-dvh max-w-3xl mx-auto px-4 py-6 space-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {/* The brand blue is pinned to the mark rather than taken from
              `text-primary` so it reads the same as the tab icon and the
              manifest theme_color, in either colour scheme. */}
          <SkipMark className="text-[#2f6fed]" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Loads</h1>
            <p className="text-sm text-muted-foreground">Capture a ticket, add your pay</p>
          </div>
        </div>
        <div className="flex items-center">
          {/* Replay is deliberately always available — the stored flag only
              governs the automatic first run. */}
          <button
            type="button"
            onClick={() => setTourOpen(true)}
            aria-label="How it works"
            className="p-2 text-muted-foreground"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => signOut().then(() => window.location.assign("/auth"))}
            aria-label="Sign out"
            className="p-2 -mr-2 text-muted-foreground"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      <Link href="/capture">
        <Button data-tour="capture" size="lg" className="w-full h-14 text-base gap-3">
          <Camera className="h-5 w-5" />
          Capture load
        </Button>
      </Link>

      {/* The report is what the day builds toward — dispatch verifies its
          total against the settlement — so it sits right under capture. */}
      <Link href="/report">
        <Button data-tour="report" variant="outline" size="lg" className="w-full gap-2">
          <FileText className="h-4 w-4" />
          Report
        </Button>
      </Link>

      <section className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Your loads
          </h2>
          {data && data.loads.length > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {formatAmount(String(pricedTotal))} total
              {needAmount > 0 && (
                <span className="text-amber-600 dark:text-amber-500">
                  {" "}· {needAmount} need{needAmount === 1 ? "s" : ""} an amount
                </span>
              )}
            </span>
          )}
        </div>

        {isPending && (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {isError && (
          <Card className="p-4 text-sm text-destructive" role="alert">
            Couldn&apos;t load your loads: {describeError(error)}
          </Card>
        )}

        {data && data.loads.length === 0 && (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            No loads yet. Capture a ticket to get started.
          </Card>
        )}

        {rows.length > 0 && (
          <ul className="space-y-2">
            {rows.map((row) => (
              <LoadListItem key={row.id} row={row} />
            ))}
          </ul>
        )}
      </section>

      <CoachTour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        userId={userId}
        storage={storage}
      />
    </div>
  );
}

function LoadListItem({ row }: { row: LoadListDisplayRow }) {
  return (
    <li>
      <Link href={row.href}>
        <Card
          className={
            "p-4 flex items-center justify-between gap-4 active:bg-accent transition-colors" +
            (row.lowConfidence ? " border-amber-400/60 dark:border-amber-500/50" : "")
          }
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 font-medium">
              <Container className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="truncate font-mono">{row.containerLabel}</span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground flex flex-wrap items-center gap-x-2">
              <span>{row.metaLabel}</span>
              {row.lowConfidence && (
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-label="Low confidence" />
              )}
            </div>
          </div>

          <div className="shrink-0">
            {row.needsAmount ? (
              // Not a placeholder dash — an unpriced load is the driver's
              // to-do, so the empty state is the call to action.
              <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                <Plus className="h-4 w-4" />
                Add amount
              </span>
            ) : (
              <span className="text-base font-semibold tabular-nums">{row.amountLabel}</span>
            )}
          </div>
        </Card>
      </Link>
    </li>
  );
}
